/**
 * Replaying the day: the last window of the action log, played back fast as the map opens.
 *
 * The ledger answers "what happened"; replaying it is what makes the map open ALIVE rather than as a
 * diagram. The schedule compresses however many hours into a few seconds: events keep their order, the
 * gaps between them are squeezed to a band (so a quiet morning does not stall the reel and a burst does
 * not become a blur), and the whole reel is capped. Live events that arrive during the reel are queued
 * behind it — see `mergeLive` — so the reader never sees a "now" particle overtaken by a "this morning" one.
 *
 * Pure and mirrored on iOS (`MapReplay.swift`) and Android (`MapReplay.kt`).
 */
import type { MapEvent } from './map-events';

/** How far back the opening replay reaches. */
export const REPLAY_WINDOW_MS = 24 * 60 * 60 * 1000;
/** The gap between two replayed particles, squeezed into this band. */
export const REPLAY_MIN_GAP_MS = 140;
export const REPLAY_MAX_GAP_MS = 420;
/** The whole reel never takes longer than this; past it the gaps shrink evenly (never below the minimum,
 *  which simply drops the oldest rows instead). */
export const REPLAY_MAX_TOTAL_MS = 7000;
/** How many rows the reel will show at most — the ledger is pulled newest-first, so these are the latest. */
export const REPLAY_MAX_EVENTS = 40;
/** One particle's flight; the schedule's offsets are launch times, and a launch may overlap the last flight. */
export const PARTICLE_FLIGHT_MS = 1100;

export interface ScheduledEvent {
  event: MapEvent;
  /** Launch offset from the reel's start, ms. */
  launchAt: number;
}

/**
 * Order the given events oldest→newest inside the window and assign launch offsets. `now` bounds the
 * window; rows outside it are dropped. Deterministic for a given input.
 */
export function scheduleReplay(events: MapEvent[], now: number): ScheduledEvent[] {
  const since = now - REPLAY_WINDOW_MS;
  const inWindow = events.filter((e) => e.at >= since && e.at <= now).sort((a, b) => a.at - b.at);
  const latest = inWindow.slice(-REPLAY_MAX_EVENTS);
  if (latest.length === 0) return [];

  // Real gaps → squeezed gaps.
  const gaps: number[] = [];
  for (let i = 1; i < latest.length; i++) {
    const real = latest[i].at - latest[i - 1].at;
    // Map a real gap in [0, 1h] onto the band; anything longer than an hour is "a while" and gets the max.
    const t = Math.min(Math.max(real / (60 * 60 * 1000), 0), 1);
    gaps.push(REPLAY_MIN_GAP_MS + (REPLAY_MAX_GAP_MS - REPLAY_MIN_GAP_MS) * t);
  }
  let total = gaps.reduce((a, b) => a + b, 0);
  // Fit the cap by scaling gaps down, but never below the minimum.
  if (total > REPLAY_MAX_TOTAL_MS) {
    const scale = REPLAY_MAX_TOTAL_MS / total;
    for (let i = 0; i < gaps.length; i++) gaps[i] = Math.max(REPLAY_MIN_GAP_MS, gaps[i] * scale);
    total = gaps.reduce((a, b) => a + b, 0);
  }
  // If the minimum-gap floor still overflows the cap, drop the oldest rows until it fits.
  let start = 0;
  while (total > REPLAY_MAX_TOTAL_MS && start < gaps.length) {
    total -= gaps[start];
    start++;
  }
  const kept = latest.slice(start);
  const keptGaps = gaps.slice(start);
  const out: ScheduledEvent[] = [];
  let offset = 0;
  kept.forEach((event, i) => {
    if (i > 0) offset += keptGaps[i - 1];
    out.push({ event, launchAt: Math.round(offset) });
  });
  return out;
}

/** The reel's total length: the last launch plus its flight. */
export function replayDuration(schedule: ScheduledEvent[]): number {
  if (schedule.length === 0) return 0;
  return schedule[schedule.length - 1].launchAt + PARTICLE_FLIGHT_MS;
}

/**
 * A live event arriving while the reel plays launches after the reel ends (never in the middle of it);
 * one arriving after the reel launches at once. Returns the launch offset from the reel's start.
 */
export function mergeLive(schedule: ScheduledEvent[], elapsedMs: number): number {
  const end = replayDuration(schedule);
  return elapsedMs >= end ? elapsedMs : end;
}
